import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  CalendarClock,
  Check,
  ChevronDown,
  ChevronRight,
  Copy,
  FileCheck2,
  FileText,
  HelpCircle,
  Layers,
  ListTree,
  Loader2,
  Lock,
  Network,
  Paperclip,
  Plus,
  Sparkles,
  Users,
  X,
} from 'lucide-react';
import { ApiError, apiFetch } from '../lib/api';
import { sesionApi } from '../lib/sesion';
import {
  documentosApi,
  documentosKeys,
  indicePorBloque,
  type ComentarioValidacion,
} from '../lib/documentos';
import { EsqueletoLineas } from './ui/Esqueleto';
import { Confirmar } from './ui/Confirmar';
import { DiffProcedencia } from './validacion/DiffProcedencia';
import {
  cambiosApi,
  cambiosKeys,
  corridaTerminada,
  ETIQUETA_ESTADO_IMPACTO,
  type EstadoCorridaSintesis,
  type ResolucionImpacto,
} from '../lib/cambios';

/**
 * VALIDACIÓN DE DOCUMENTOS — el panel del administrador (Cabina).
 *
 * Crea validaciones (una por documento), reparte sus links y las cierra.
 * Cada link abre el módulo de grabación personal con login Google: los
 * comentarios de todos caen en la actividad de ESE documento, con autor y
 * transcripción, sin que nadie tenga que conducir una sala. «Cerrar y mandar
 * a acta» usa la máquina de estados de siempre: congela el acta con todo lo
 * recibido.
 *
 * V1 — VALIDACIÓN SOBRE BLOQUES. Se le agregan dos cosas, las dos OPCIONALES:
 *
 *  1. Al crear la validación se puede adjuntar el documento. Se sube primero
 *     (`POST /procesos/{id}/documentos`, que lo parte en párrafos y publica su
 *     versión 1) y recién después se crea la validación apuntando a esa
 *     versión. El número de párrafos que devuelve la ingesta es la señal de
 *     que el documento se leyó bien: se muestra siempre.
 *  2. Cuando la validación tiene documento, sus comentarios se pueden ver
 *     agrupados por párrafo en vez de como un único total.
 *
 * V4 y V5 — LO QUE SE LE AGREGA AHORA, y que sigue igual de opcional:
 *
 *  3. **Por sección**, no solo por párrafo. Una sección con doce comentarios
 *     repartidos en seis párrafos no se entiende leyendo párrafo por párrafo:
 *     se lee «el capítulo de Tomas de posesión se llevó doce voces». La
 *     agrupación sale de `GET /validaciones/{id}/secciones`, que es un GROUP BY
 *     determinista — agrupar no gasta una corrida de IA (regla dura).
 *  4. **Sintetizar** (`POST /validaciones/{id}/sintetizar`): UNA corrida por
 *     lote, nunca una por comentario (D25). Tarda minutos, así que el botón no
 *     se queda mudo: mientras corre se ve el estado real de la fila del motor.
 *  5. **El diff de cuatro capas** (SSOT §5.8) por cada cambio propuesto, que
 *     dibuja {@link DiffProcedencia}.
 *  6. **El tablero de impactos** (§5.6): aprobar un cambio no guarda un texto,
 *     contrae deudas. Mientras queden impactos pendientes la versión no se
 *     cierra (D08) y eso se dice en pantalla, arriba, no escondido en un tab.
 *
 * REGLA DE ORO — crear una validación SIN documento sigue siendo el camino por
 * defecto y no cambió en nada: mismo formulario, mismo cuerpo (`documento_
 * version_id` ni siquiera se manda), misma fila en la lista. Las validaciones
 * del Consejo que ya están recibiendo comentarios no tienen documento cargado,
 * así que no muestran ni el desplegable, ni las vistas, ni el botón de
 * sintetizar, ni el tablero de impactos: se ven y funcionan exactamente como
 * ayer. TODO lo de V4/V5 cuelga de `v.documento_version_id`, y esa comprobación
 * está una sola vez, en {@link VistasDelDocumento}, para que no se pueda colar
 * media función por olvidar un `&&`.
 */

interface Validacion {
  actividad_id: string;
  sesion_id: string;
  titulo: string;
  estado: string;
  cierra_el: string | null;
  link: string;
  aportes: number;
  personas: number;
  /** null = validación del modo de siempre (comentario sobre el documento entero). */
  documento_version_id?: string | null;
  documento_titulo?: string | null;
  /** Párrafos de la versión enganchada. 0 cuando no hay documento. */
  bloques?: number;
}

/** Documento ya ingerido y todavía sin validación: sobrevive a un reintento. */
interface DocumentoCargado {
  versionId: string;
  bloques: number;
  nombre: string;
}

const claves = {
  lista: (procesoId: string) => ['validaciones', procesoId] as const,
};

// ─── Vocabulario de V4/V5 ───────────────────────────────────────────────────
// Los tipos, el cliente HTTP y las etiquetas de clase/estado NO se redeclaran
// acá: viven en `lib/cambios.ts`, que es de donde también los lee el diff. Dos
// copias del mismo contrato divergen el día que el backend agrega un campo.

/** Las cuatro maneras de mirar un documento comentado. */
type Vista = 'parrafos' | 'secciones' | 'cambios' | 'impactos';

/** Cómo se llama en la sala cada vía por la que el grafo encontró una relación. */
const NOMBRE_ARISTA: Record<string, string> = {
  referencia_cruzada: 'lo nombra por su código',
  valor_compartido: 'citan la misma norma o el mismo plazo',
  termino_compartido: 'hablan del mismo concepto',
  dependencia: 'dependencia inferida',
};


function plural(n: number, singular: string, plural_: string): string {
  return `${n} ${n === 1 ? singular : plural_}`;
}

/**
 * Lo que la ingesta sabe leer hoy: EXACTAMENTE lo mismo que acepta el backend
 * (`ingesta_documento.EXTENSIONES` y `MAX_BYTES`). Ser más permisivo acá sería
 * dejar que alguien espere una subida entera para recibir un 415 al final; el
 * `.doc` binario de Word 97 no entra: python-docx no lo lee.
 */
const EXTENSIONES = ['docx', 'pdf', 'txt', 'md'];
const MAX_BYTES = 40 * 1024 * 1024;

function extensionDe(nombre: string): string {
  const i = nombre.lastIndexOf('.');
  return i > 0 ? nombre.slice(i + 1).toLowerCase() : '';
}

/** El mensaje del backend gana siempre: viene escrito para una persona. */
function mensajeDeError(e: unknown, fallback: string): string {
  if (e instanceof ApiError) return e.detail;
  if (e instanceof Error && e.message) return e.message;
  return fallback;
}

export function PanelValidaciones({ procesoId }: { procesoId: string }) {
  const qc = useQueryClient();
  const [titulo, setTitulo] = useState('');
  const [fecha, setFecha] = useState('');
  const [copiado, setCopiado] = useState<string | null>(null);
  const [archivo, setArchivo] = useState<File | null>(null);
  const [cargado, setCargado] = useState<DocumentoCargado | null>(null);
  // Espejo del anterior para leerlo DENTRO de la mutación (ver comentario de
  // `crear`). El estado es para pintar; el ref es la verdad.
  const cargadoRef = useRef<DocumentoCargado | null>(null);
  const recordarCargado = (v: DocumentoCargado | null) => {
    cargadoRef.current = v;
    setCargado(v);
  };
  const [fase, setFase] = useState<'subiendo' | 'creando' | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Qué validación está abierta y en qué vista. Una sola a la vez: dos
      documentos desplegados en paralelo no se comparan, se confunden. */
  const [panel, setPanel] = useState<{ id: string; vista: Vista } | null>(null);
  /**
   * La síntesis en curso, si la hay. Vive ACÁ arriba y no dentro de la vista de
   * cambios a propósito: la corrida tarda minutos y quien la pidió va a seguir
   * trabajando —cambiar de vista, plegar la fila— mientras espera. Si el estado
   * viviera en la vista, plegarla desmontaría el sondeo y la corrida seguiría
   * corriendo en el servidor sin nadie mirándola.
   *
   * Una sola, no un mapa por validación: el motor serializa con `agora:ia:lock`
   * y de todas formas corre de a una.
   */
  const [sintesis, setSintesis] = useState<{
    actividadId: string;
    corrida: EstadoCorridaSintesis;
  } | null>(null);
  const [errorSintesis, setErrorSintesis] = useState<string | null>(null);
  /** Validación cuyo cierre está esperando confirmación. Cerrar manda a acta y
      congela los comentarios: no se deshace. */
  const [porCerrar, setPorCerrar] = useState<Validacion | null>(null);
  const campoArchivo = useRef<HTMLInputElement | null>(null);
  const idArchivo = useId();

  const lista = useQuery({
    queryKey: claves.lista(procesoId),
    queryFn: () => apiFetch<Validacion[]>(`/procesos/${procesoId}/validaciones`),
    refetchInterval: 30_000,
  });

  /**
   * Dos pasos, en este orden y solo si hay archivo: subir el documento (la
   * ingesta lo parte en párrafos) y crear la validación apuntando a la versión
   * que quedó publicada. Sin archivo es exactamente la llamada de siempre.
   *
   * Si el segundo paso falla, el documento YA se ingirió y ya gastó cupo: se
   * guarda para que el reintento no lo suba dos veces.
   *
   * Ese recuerdo vive en un ref, no en el estado: `mutationFn` lee del closure
   * del render en que se creó, y `setCargado` no lo actualiza. Como el cliente
   * de queries reintenta las mutaciones una vez, con estado el reintento veía
   * `null`, volvía a ingerir el documento y gastaba dos veces el cupo del mes.
   */
  const crear = useMutation({
    mutationFn: async () => {
      const nombre = titulo.trim();
      let versionId = cargadoRef.current?.versionId ?? null;
      let bloques = cargadoRef.current?.bloques ?? 0;

      if (archivo && !versionId) {
        setFase('subiendo');
        const doc = await documentosApi.subir(procesoId, { file: archivo, titulo: nombre });
        if (!doc.version_actual) {
          throw new Error(
            'El documento se cargó pero no pudimos leer sus párrafos. Probá con el .docx original, o creá la validación sin documento.',
          );
        }
        versionId = doc.version_actual.version_id;
        bloques = doc.bloques;
        recordarCargado({ versionId, bloques, nombre: archivo.name });
      }

      setFase('creando');
      const validacion = await apiFetch<Validacion>(`/procesos/${procesoId}/validaciones`, {
        method: 'POST',
        json: {
          titulo: nombre,
          cierra_el: fecha || null,
          // La clave solo viaja si hay documento: sin ella el backend crea la
          // validación del modo de siempre, sin tocar nada.
          ...(versionId ? { documento_version_id: versionId } : {}),
        },
      });
      return { validacion, bloques: versionId ? bloques : 0 };
    },
    onMutate: () => {
      setError(null);
      setAviso(null);
    },
    onSuccess: ({ validacion, bloques }) => {
      setTitulo('');
      setFecha('');
      setArchivo(null);
      recordarCargado(null);
      if (campoArchivo.current) campoArchivo.current.value = '';
      setAviso(
        bloques > 0
          ? `«${validacion.titulo}» quedó lista: ${bloques} párrafo${bloques === 1 ? '' : 's'} para comentar uno por uno.`
          : `«${validacion.titulo}» quedó lista. Se comenta sobre el documento completo.`,
      );
      void qc.invalidateQueries({ queryKey: claves.lista(procesoId) });
    },
    onError: (e) =>
      setError(mensajeDeError(e, 'No se pudo crear. Revisá el nombre e intentá de nuevo.')),
    onSettled: () => setFase(null),
  });

  /** Portero local: el mismo criterio del backend, dicho antes de gastar la subida. */
  const elegirArchivo = (f: File | null) => {
    setError(null);
    setAviso(null);
    recordarCargado(null); // si cambia el archivo, hay que ingerirlo de nuevo
    if (!f) {
      setArchivo(null);
      return;
    }
    if (!EXTENSIONES.includes(extensionDe(f.name))) {
      setArchivo(null);
      if (campoArchivo.current) campoArchivo.current.value = '';
      setError('Por ahora leemos documentos de Word (.docx) y PDF. Convertí el archivo y volvé a elegirlo.');
      return;
    }
    if (f.size > MAX_BYTES) {
      setArchivo(null);
      if (campoArchivo.current) campoArchivo.current.value = '';
      setError('El documento pasa de 40 MB. Subí una versión más liviana o partilo en dos.');
      return;
    }
    setArchivo(f);
    if (!titulo.trim()) setTitulo(f.name.replace(/\.[^.]+$/, ''));
  };

  const quitarArchivo = () => {
    setArchivo(null);
    recordarCargado(null);
    setError(null);
    if (campoArchivo.current) campoArchivo.current.value = '';
  };

  const cerrar = useMutation({
    mutationFn: (actividadId: string) => sesionApi.cerrar(actividadId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: claves.lista(procesoId) }),
  });

  const ajustarFecha = useMutation({
    mutationFn: (v: { actividadId: string; cierra_el: string | null }) =>
      apiFetch<Validacion>(`/validaciones/${v.actividadId}`, {
        method: 'PATCH',
        json: { cierra_el: v.cierra_el },
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: claves.lista(procesoId) }),
  });

  /**
   * D25 — UNA corrida para todo el lote. El botón manda a la fila la
   * consolidación de TODOS los comentarios anclados de la validación; jamás uno
   * por comentario. Lo que vuelve es el estado de la corrida, no la síntesis:
   * el análisis corre en el worker del host y puede tener otro adelante.
   */
  const sintetizar = useMutation({
    mutationFn: (actividadId: string) => cambiosApi.sintetizar(actividadId),
    onMutate: () => setErrorSintesis(null),
    onSuccess: (corrida, actividadId) => setSintesis({ actividadId, corrida }),
    onError: (e) =>
      setErrorSintesis(
        mensajeDeError(
          e,
          'No pudimos pedir la síntesis. Los comentarios están a salvo; reintentá en un momento.',
        ),
      ),
  });

  // `sin_cupo` y `pendiente_reintento` NO son finales: la corrida no se perdió,
  // se reanuda sola. Dejar de preguntar ahí congelaría la pantalla en «sin cupo»
  // aunque el análisis ya hubiera terminado.
  const corridaViva = sintesis !== null && !corridaTerminada(sintesis.corrida.estado);

  const seguimiento = useQuery({
    queryKey: cambiosKeys.corrida(sintesis?.corrida.corrida_id ?? ''),
    queryFn: () => cambiosApi.estadoCorrida(sintesis!.corrida.corrida_id),
    enabled: corridaViva,
    // Cinco segundos: lo bastante seguido para que la posición en la fila se
    // mueva a la vista, lo bastante espaciado para no castigar al motor durante
    // los veinte minutos que puede durar una síntesis.
    refetchInterval: 5_000,
  });

  // El sondeo escribe en el estado y, cuando termina bien, tira la caché de las
  // dos vistas que la síntesis acaba de cambiar. La guarda del principio no es
  // decorativa: sin ella cada `setSintesis` volvería a disparar este efecto.
  const nuevoEstado = seguimiento.data;
  useEffect(() => {
    if (!nuevoEstado || !sintesis) return;
    const previo = sintesis.corrida;
    if (
      previo.estado === nuevoEstado.estado &&
      previo.mensaje === nuevoEstado.mensaje &&
      previo.posicion === nuevoEstado.posicion
    ) {
      return;
    }
    setSintesis({ actividadId: sintesis.actividadId, corrida: { ...previo, ...nuevoEstado } });
    if (nuevoEstado.estado === 'ok') {
      void qc.invalidateQueries({ queryKey: ['validacion-cambios', sintesis.actividadId] });
      void qc.invalidateQueries({ queryKey: ['validacion-impactos', sintesis.actividadId] });
    }
  }, [nuevoEstado, sintesis, qc]);

  const copiarLink = async (v: Validacion) => {
    const url = `${window.location.origin}${v.link}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiado(v.actividad_id);
      setTimeout(() => setCopiado(null), 2500);
    } catch {
      window.prompt('Copiá el link:', url);
    }
  };

  return (
    <section className="rounded-xl border border-border bg-surface-raised p-4">
      <h3 className="flex items-center gap-2 font-display text-base font-semibold text-content">
        <FileCheck2 className="h-5 w-5 text-navy" aria-hidden="true" />
        Validación de documentos
      </h3>
      <p className="mt-1 font-sans text-xs text-content-muted">
        Un link por documento: quien lo abre entra con Google y sus comentarios
        —voz o texto— caen aquí con su nombre y transcripción. No requiere
        conducir sala; cerrás cuando decidás y el acta se congela con todo lo
        recibido.
      </p>

      {/* crear */}
      <form
        className="mt-3 flex flex-wrap items-end gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          if (titulo.trim().length >= 3) crear.mutate();
        }}
      >
        <label className="flex min-w-[220px] flex-1 flex-col gap-1 font-sans text-xs text-content-muted">
          Documento a validar
          <input
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            placeholder="SCCJ-Requerimientos-de-Sistemas-v0.1"
            className="rounded-lg border border-border bg-surface-base px-3 py-2 font-sans text-sm text-content placeholder:text-content-muted focus:border-navy focus:outline-none"
          />
        </label>
        <label className="flex flex-col gap-1 font-sans text-xs text-content-muted">
          Recibe hasta (opcional)
          <input
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            className="rounded-lg border border-border bg-surface-base px-3 py-2 font-sans text-sm text-content focus:border-navy focus:outline-none"
          />
        </label>
        {/* Adjuntar el documento es OPCIONAL: sin archivo, la validación se
            crea igual que siempre y se comenta sobre el documento completo. */}
        <div className="flex min-w-[220px] flex-1 flex-col gap-1 font-sans text-xs text-content-muted">
          Archivo (opcional: habilita comentar párrafo por párrafo)
          <input
            ref={campoArchivo}
            type="file"
            accept=".docx,.pdf,.txt,.md"
            className="sr-only"
            id={idArchivo}
            onChange={(e) => elegirArchivo(e.target.files?.[0] ?? null)}
          />
          {archivo ? (
            <span className="inline-flex min-h-touch items-center gap-1.5 rounded-lg border border-navy bg-navy/5 px-3 py-2 font-sans text-sm text-content">
              <FileText className="h-4 w-4 shrink-0 text-navy" aria-hidden="true" />
              <span className="min-w-0 flex-1 truncate">{archivo.name}</span>
              <button
                type="button"
                onClick={quitarArchivo}
                disabled={crear.isPending}
                aria-label={`Quitar ${archivo.name}`}
                className="rounded p-1 text-content-muted transition-colors duration-fast hover:text-error disabled:opacity-40"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </span>
          ) : (
            <label
              htmlFor={idArchivo}
              className="inline-flex min-h-touch cursor-pointer items-center gap-1.5 rounded-lg border border-dashed border-border-strong bg-surface-base px-3 py-2 font-sans text-sm text-content-muted transition-colors duration-fast hover:border-navy hover:text-content"
            >
              <Paperclip className="h-4 w-4 shrink-0" aria-hidden="true" />
              Adjuntar .docx o .pdf
            </label>
          )}
        </div>
        <button
          type="submit"
          disabled={titulo.trim().length < 3 || crear.isPending}
          className="inline-flex min-h-[40px] items-center gap-1.5 rounded-lg bg-navy px-4 py-2 font-sans text-sm font-semibold text-white transition hover:bg-navy/90 disabled:opacity-40"
        >
          {crear.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Plus className="h-4 w-4" aria-hidden="true" />
          )}
          Crear y obtener link
        </button>
      </form>

      {/* Progreso: la subida y la lectura del documento pueden tardar. */}
      {fase && (
        <p className="mt-1.5 flex items-center gap-1.5 font-sans text-xs text-content-muted">
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          {fase === 'subiendo'
            ? `Subiendo y leyendo «${archivo?.name ?? 'el documento'}»: se parte en párrafos, puede tardar unos segundos.`
            : 'Creando la validación y su link…'}
        </p>
      )}

      {/* El documento ya se ingirió pero la validación no llegó a crearse: se
          avisa para que el reintento no vuelva a gastar cupo. */}
      {!fase && cargado && (
        <p className="mt-1.5 font-sans text-xs text-content-muted">
          «{cargado.nombre}» ya quedó cargado ({cargado.bloques} párrafo
          {cargado.bloques === 1 ? '' : 's'}). Volvé a darle a «Crear y obtener link»: no se
          sube de nuevo.
        </p>
      )}

      {error && (
        <p
          role="alert"
          className="mt-1.5 rounded-lg border border-error px-2.5 py-1.5 font-sans text-xs text-error"
        >
          {error}
        </p>
      )}
      {aviso && !error && (
        <p className="mt-1.5 flex items-start gap-1.5 rounded-lg bg-success/10 px-2.5 py-1.5 font-sans text-xs text-success">
          <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {aviso}
        </p>
      )}

      {/* lista */}
      {lista.isLoading ? (
        <p className="mt-3 flex items-center gap-2 font-sans text-sm text-content-muted">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          Cargando validaciones…
        </p>
      ) : (lista.data ?? []).length === 0 ? (
        <p className="mt-3 rounded-lg border border-dashed border-border px-3 py-2 font-sans text-sm text-content-muted">
          Aún no hay validaciones en este proceso. Creá la primera con el nombre
          del documento.
        </p>
      ) : (
        <ul className="mt-3 space-y-2">
          {(lista.data ?? []).map((v) => (
            <li
              key={v.actividad_id}
              className="rounded-lg border border-border bg-surface-base px-3 py-2.5"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="min-w-0 flex-1 truncate font-sans text-sm font-medium text-content">
                  {v.titulo}
                </span>
                {v.estado === 'abierta' ? (
                  <span className="rounded-full bg-success/10 px-2 py-0.5 font-sans text-[11px] font-semibold text-success">
                    LINK VIVO
                  </span>
                ) : (
                  <span className="rounded-full bg-border px-2 py-0.5 font-sans text-[11px] font-semibold text-content-muted">
                    EN ACTA
                  </span>
                )}
              </div>
              <p className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 font-sans text-xs text-content-muted">
                <span className="inline-flex items-center gap-1">
                  <Users className="h-3.5 w-3.5" aria-hidden="true" />
                  {v.personas} persona{v.personas === 1 ? '' : 's'} · {v.aportes} comentario
                  {v.aportes === 1 ? '' : 's'}
                </span>
                {v.documento_version_id && (
                  <span className="inline-flex items-center gap-1">
                    <FileText className="h-3.5 w-3.5" aria-hidden="true" />
                    {v.bloques ?? 0} párrafo{(v.bloques ?? 0) === 1 ? '' : 's'}
                  </span>
                )}
                {v.cierra_el && (
                  <span className="inline-flex items-center gap-1">
                    <CalendarClock className="h-3.5 w-3.5" aria-hidden="true" />
                    recibe hasta {v.cierra_el}
                  </span>
                )}
              </p>

              {/* TODO lo de V1/V4/V5 vive detrás de esta única comprobación. Sin
                  documento no hay párrafos que agrupar, ni secciones, ni
                  síntesis, ni impactos: la fila queda exactamente como siempre. */}
              {v.documento_version_id && (
                <VistasDelDocumento
                  actividadId={v.actividad_id}
                  vista={panel?.id === v.actividad_id ? panel.vista : null}
                  onVista={(vista) =>
                    setPanel(vista === null ? null : { id: v.actividad_id, vista })
                  }
                  sintesis={sintesis?.actividadId === v.actividad_id ? sintesis.corrida : null}
                  sintetizando={sintetizar.isPending && sintetizar.variables === v.actividad_id}
                  corridaViva={corridaViva && sintesis?.actividadId === v.actividad_id}
                  errorSintesis={
                    sintetizar.variables === v.actividad_id ? errorSintesis : null
                  }
                  onSintetizar={() => sintetizar.mutate(v.actividad_id)}
                />
              )}
              {v.estado === 'abierta' && (
                <div className="mt-2 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => void copiarLink(v)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-navy px-3 py-1.5 font-sans text-xs font-semibold text-navy transition hover:bg-navy/5"
                  >
                    {copiado === v.actividad_id ? (
                      <>
                        <Check className="h-3.5 w-3.5" aria-hidden="true" /> Copiado
                      </>
                    ) : (
                      <>
                        <Copy className="h-3.5 w-3.5" aria-hidden="true" /> Copiar link
                      </>
                    )}
                  </button>
                  <label className="inline-flex items-center gap-1.5 font-sans text-xs text-content-muted">
                    <CalendarClock className="h-3.5 w-3.5" aria-hidden="true" />
                    <input
                      type="date"
                      defaultValue={v.cierra_el ?? ''}
                      onChange={(e) =>
                        ajustarFecha.mutate({
                          actividadId: v.actividad_id,
                          cierra_el: e.target.value || null,
                        })
                      }
                      className="rounded border border-border bg-surface-raised px-2 py-1 font-sans text-xs text-content"
                      aria-label={`Fecha límite de ${v.titulo}`}
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => setPorCerrar(v)}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-error px-3 py-1.5 font-sans text-xs font-semibold text-error transition hover:bg-error/5"
                  >
                    <Lock className="h-3.5 w-3.5" aria-hidden="true" />
                    Cerrar y mandar a acta
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}

      <Confirmar
        abierto={porCerrar !== null}
        titulo={`¿Cerrar «${porCerrar?.titulo ?? ''}» y mandar a acta?`}
        detalle={`El link deja de recibir y los ${porCerrar?.aportes ?? 0} comentarios quedan congelados en el acta. Esto no se deshace.`}
        confirmar="Cerrar y mandar a acta"
        onConfirmar={() => {
          if (porCerrar) cerrar.mutate(porCerrar.actividad_id);
          setPorCerrar(null);
        }}
        onCancelar={() => setPorCerrar(null)}
      />
    </section>
  );
}

/**
 * LA BANDEJA POR PÁRRAFO (SSOT §5).
 *
 * Deja de leerse «37 comentarios» y pasa a leerse «el párrafo 4.1 se llevó
 * once». Los conteos salen de `GET /validaciones/{id}/bloques/resumen`, que es
 * un GROUP BY sobre las anclas: SQL determinista, jamás IA (regla dura del
 * proyecto para todo agregado mecánico).
 *
 * Lo que NO se calla: los comentarios SIN ANCLAR. Son datos que entraron —voz
 * o texto— y que quedaron sin dirección: el ancla falló, o la persona comentó
 * el documento entero. El SSOT es tajante: es preferible mostrar «hay 12 sin
 * ubicar» a colgarlos en silencio del párrafo equivocado. Por eso tienen su
 * propia línea, arriba de la lista y no al final.
 *
 * Solo se monta cuando la validación tiene documento: sin él no hay párrafos
 * que agrupar y la fila muestra el total de siempre.
 */
function BandejaPorBloque({ actividadId }: { actividadId: string }) {
  const [abierto, setAbierto] = useState<string | null>(null);

  const documento = useQuery({
    queryKey: documentosKeys.deValidacion(actividadId),
    queryFn: () => documentosApi.deValidacion(actividadId),
    staleTime: 5 * 60_000, // el documento está congelado en su versión
  });

  const resumen = useQuery({
    queryKey: documentosKeys.resumen(actividadId),
    queryFn: () => documentosApi.resumen(actividadId),
    refetchInterval: 30_000,
  });

  // Lo que dijeron, no solo cuántos. Se pide una vez y se agrupa en memoria:
  // son decenas de comentarios, no miles, y así desplegar un párrafo es
  // instantáneo en vez de disparar una petición por párrafo.
  const comentarios = useQuery({
    queryKey: documentosKeys.comentarios(actividadId),
    queryFn: () => documentosApi.comentarios(actividadId),
    refetchInterval: 30_000,
  });

  const porBloque = useMemo(() => {
    const m = new Map<string, ComentarioValidacion[]>();
    for (const c of comentarios.data ?? []) {
      if (!c.bloque_id_estable) continue;
      const lista = m.get(c.bloque_id_estable);
      if (lista) lista.push(c);
      else m.set(c.bloque_id_estable, [c]);
    }
    return m;
  }, [comentarios.data]);

  const sinUbicar = useMemo(
    () => (comentarios.data ?? []).filter((c) => !c.bloque_id_estable),
    [comentarios.data],
  );

  if (documento.isLoading || resumen.isLoading) {
    return <EsqueletoLineas lineas={4} className="mt-2" />;
  }

  if (documento.isError || resumen.isError) {
    return (
      <p className="mt-2 font-sans text-xs text-error">
        No pudimos traer los comentarios por párrafo. Los comentarios están a salvo;
        reintentá en un momento.
      </p>
    );
  }

  const doc = documento.data;
  const res = resumen.data;
  if (!doc || !res) {
    return (
      <p className="mt-2 font-sans text-xs text-content-muted">
        Esta validación no tiene un documento legible cargado: los comentarios entran
        sobre el documento completo.
      </p>
    );
  }

  const indice = indicePorBloque(res);
  const conComentarios = doc.bloques.filter(
    (b) => (indice.get(b.id_estable)?.comentarios ?? 0) > 0,
  );
  const cobertura = Math.round(res.cobertura * 100);

  return (
    <div className="despliega mt-2 rounded-lg border border-border bg-surface-raised p-2.5">
      <p className="font-sans text-[11px] uppercase tracking-wide text-content-muted">
        {res.bloques_con_comentario} de {res.bloques_total} párrafos con comentarios ·{' '}
        {cobertura}% del documento
      </p>

      {res.comentarios_sin_anclar > 0 && (
        <p className="mt-2 flex items-start gap-1.5 rounded-lg border border-warning px-2.5 py-1.5 font-sans text-xs text-content">
          <HelpCircle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" aria-hidden="true" />
          <span>
            <strong className="font-semibold">
              {res.comentarios_sin_anclar} comentario{res.comentarios_sin_anclar === 1 ? '' : 's'}{' '}
              sin ubicar
            </strong>
            : entraron sobre el documento completo. Alguien tiene que decidir de qué
            párrafo hablan; hasta entonces cuentan en el acta pero no en ningún párrafo.
            {sinUbicar.length > 0 && (
              <ul className="mt-1.5 space-y-1">
                {sinUbicar.map((c) => (
                  <li key={c.aporte_id} className="text-content-muted">
                    <span className="font-semibold text-content">{c.autor ?? 'Sin autor'}</span>
                    {': '}
                    {c.texto ?? 'Transcribiendo la voz…'}
                  </li>
                ))}
              </ul>
            )}
          </span>
        </p>
      )}

      {conComentarios.length === 0 ? (
        <p className="mt-2 rounded-lg border border-dashed border-border px-2.5 py-1.5 font-sans text-xs text-content-muted">
          Todavía nadie comentó un párrafo en particular.
        </p>
      ) : (
        <ul className="mt-2 space-y-1.5">
          {conComentarios.map((b) => {
            const conteo = indice.get(b.id_estable);
            const expandido = abierto === b.id_estable;
            return (
              <li key={b.id_estable} className="rounded-lg bg-surface-sunken px-2.5 py-2">
                <div className="flex items-start gap-2">
                  <span className="shrink-0 rounded bg-navy/10 px-1.5 py-0.5 font-sans text-[11px] font-semibold text-navy">
                    {/* La numeración propia del documento manda; si no la trae,
                        el número de párrafo (la ingesta numera 1..N). */}
                    {b.ref ?? `¶ ${b.orden}`}
                  </span>
                  <button
                    type="button"
                    onClick={() => setAbierto(expandido ? null : b.id_estable)}
                    aria-expanded={expandido}
                    className="min-w-0 flex-1 text-left font-sans text-xs leading-relaxed text-content"
                  >
                    <span className={expandido ? '' : 'line-clamp-2'}>{b.texto}</span>
                  </button>
                </div>
                <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 pl-1 font-sans text-[11px] text-content-muted">
                  <span className="inline-flex items-center gap-1 font-semibold text-content">
                    <FileCheck2 className="h-3 w-3" aria-hidden="true" />
                    {conteo?.comentarios ?? 0} comentario
                    {(conteo?.comentarios ?? 0) === 1 ? '' : 's'}
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <Users className="h-3 w-3" aria-hidden="true" />
                    {conteo?.personas ?? 0} persona{(conteo?.personas ?? 0) === 1 ? '' : 's'}
                  </span>
                </p>
                {expandido && (
                  <ul className="despliega mt-1.5 space-y-1.5 border-l-2 border-navy/20 pl-2.5">
                    {(porBloque.get(b.id_estable) ?? []).map((c) => (
                      <li key={c.aporte_id}>
                        <p className="font-sans text-[11px] font-semibold text-content">
                          {c.autor ?? 'Sin autor'}
                          {c.tiene_audio && (
                            <span className="ml-1.5 font-normal text-content-muted">· de voz</span>
                          )}
                          {c.origen_ancla === 'manual' && (
                            <span className="ml-1.5 font-normal text-content-muted">
                              · ubicado por quien facilita
                            </span>
                          )}
                        </p>
                        <p className="font-sans text-xs leading-relaxed text-content-muted">
                          {c.texto ?? 'Transcribiendo la voz…'}
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/**
 * LAS CUATRO MANERAS DE MIRAR UN DOCUMENTO COMENTADO (V1, V4 y V5).
 *
 * Esta es la ÚNICA puerta de todo lo nuevo: se monta solo cuando la validación
 * tiene documento, así que las tres validaciones vivas del Consejo —99
 * comentarios sobre el documento entero, sin documento cargado en ÁGORA— nunca
 * llegan hasta acá y siguen viéndose como el primer día.
 *
 * Las cuatro vistas no son cuatro filtros del mismo dato: son cuatro preguntas
 * distintas, y por eso son cuatro vistas y no un acordeón con todo adentro.
 *
 *   Por párrafo   ¿qué se dijo de ESTE párrafo?          (V1)
 *   Por sección   ¿qué se dijo de ESTE capítulo?         (V4 · GROUP BY, sin IA)
 *   Cambios       ¿qué salió de todo eso, y quién lo pidió?  (V4 · §5.8)
 *   Impactos      ¿qué rompió lo que ya aprobamos?       (V5 · §5.6)
 *
 * El aviso de impactos pendientes se pide SIEMPRE, esté abierta o no la vista
 * de impactos. Es lo único de esta pantalla que impide cerrar la versión (D08):
 * enterarse no puede depender de que a alguien se le ocurra abrir un tab.
 */
function VistasDelDocumento({
  actividadId,
  vista,
  onVista,
  sintesis,
  sintetizando,
  corridaViva,
  errorSintesis,
  onSintetizar,
}: {
  actividadId: string;
  /** Null = la fila está plegada. */
  vista: Vista | null;
  onVista: (v: Vista | null) => void;
  /** Estado de la corrida de síntesis de ESTA validación, si hay una. */
  sintesis: EstadoCorridaSintesis | null;
  sintetizando: boolean;
  corridaViva: boolean;
  errorSintesis: string | null;
  onSintetizar: () => void;
}) {
  const impactos = useQuery({
    queryKey: cambiosKeys.impactos(actividadId),
    queryFn: () => cambiosApi.impactos(actividadId),
    refetchInterval: 60_000,
  });
  const pendientes = impactos.data?.pendientes ?? 0;

  const tabs: { clave: Vista; nombre: string; Icono: typeof FileText; insignia?: number }[] = [
    { clave: 'parrafos', nombre: 'Por párrafo', Icono: FileText },
    { clave: 'secciones', nombre: 'Por sección', Icono: ListTree },
    { clave: 'cambios', nombre: 'Cambios propuestos', Icono: Layers },
    { clave: 'impactos', nombre: 'Impactos', Icono: Network, insignia: pendientes },
  ];

  return (
    <div className="mt-2">
      <div className="flex flex-wrap items-center gap-1.5">
        {tabs.map(({ clave, nombre, Icono, insignia }) => {
          const activa = vista === clave;
          return (
            <button
              key={clave}
              type="button"
              onClick={() => onVista(activa ? null : clave)}
              aria-expanded={activa}
              className={
                activa
                  ? 'inline-flex min-h-touch items-center gap-1.5 rounded-lg border border-navy bg-navy/10 px-3 py-2 font-sans text-xs font-semibold text-navy'
                  : 'inline-flex min-h-touch items-center gap-1.5 rounded-lg border border-border bg-surface-raised px-3 py-2 font-sans text-xs font-semibold text-content-muted transition-colors duration-fast hover:border-navy hover:text-navy'
              }
            >
              <Icono className="h-3.5 w-3.5" aria-hidden="true" />
              {nombre}
              {insignia !== undefined && insignia > 0 && (
                <span
                  className="rounded-full bg-warning px-1.5 py-0.5 font-sans text-[10px] font-bold text-navy-900"
                  aria-label={`${plural(insignia, 'impacto pendiente', 'impactos pendientes')}`}
                >
                  {insignia}
                </span>
              )}
            </button>
          );
        })}
        <span className="flex-1" />
        {/* D25 — una corrida por LOTE. El botón es uno solo para toda la
            validación, no uno por comentario ni uno por párrafo. */}
        <button
          type="button"
          onClick={onSintetizar}
          disabled={sintetizando || corridaViva}
          className="inline-flex min-h-touch items-center gap-1.5 rounded-lg border border-violet bg-violet/10 px-3 py-2 font-sans text-xs font-semibold text-violet transition hover:bg-violet/20 disabled:opacity-50"
        >
          {sintetizando || corridaViva ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          ) : (
            <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
          )}
          {corridaViva ? 'Sintetizando…' : 'Sintetizar comentarios'}
        </button>
      </div>

      {/* Lo que tarda, dicho. Una síntesis puede llevar minutos y puede tener
          otro análisis adelante en la fila: dejar el botón mudo hace que la
          gente lo apriete tres veces y crea que se rompió. */}
      {sintesis && (
        <p
          className={
            sintesis.estado === 'ok'
              ? 'mt-1.5 flex items-start gap-1.5 rounded-lg bg-success/10 px-2.5 py-1.5 font-sans text-xs text-success'
              : corridaViva
                ? 'mt-1.5 flex items-start gap-1.5 rounded-lg border border-violet/40 bg-violet/5 px-2.5 py-1.5 font-sans text-xs text-content'
                : 'mt-1.5 flex items-start gap-1.5 rounded-lg border border-error px-2.5 py-1.5 font-sans text-xs text-error'
          }
          role="status"
        >
          {corridaViva ? (
            <Loader2 className="mt-0.5 h-3.5 w-3.5 shrink-0 animate-spin text-violet" aria-hidden="true" />
          ) : sintesis.estado === 'ok' ? (
            <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          ) : (
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          )}
          <span>
            {sintesis.mensaje}
            {corridaViva && sintesis.posicion > 0 && (
              <> · {plural(sintesis.posicion, 'análisis', 'análisis')} por delante en la fila</>
            )}
            {corridaViva && sintesis.eta_seg !== null && sintesis.eta_seg > 0 && (
              <> · unos {Math.max(1, Math.round(sintesis.eta_seg / 60))} min</>
            )}
            {sintesis.estado === 'ok' && (
              <>
                {' '}
                Está en <strong className="font-semibold">Cambios propuestos</strong>.
              </>
            )}
          </span>
        </p>
      )}
      {errorSintesis && (
        <p
          role="alert"
          className="mt-1.5 rounded-lg border border-error px-2.5 py-1.5 font-sans text-xs text-error"
        >
          {errorSintesis}
        </p>
      )}

      {/* D08 — la deuda se ve desde afuera, sin abrir nada. */}
      {pendientes > 0 && vista !== 'impactos' && (
        <button
          type="button"
          onClick={() => onVista('impactos')}
          className="mt-1.5 flex w-full items-start gap-1.5 rounded-lg border border-warning bg-warning/5 px-2.5 py-1.5 text-left font-sans text-xs text-content transition hover:bg-warning/10"
        >
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" aria-hidden="true" />
          <span>
            <strong className="font-semibold">
              {plural(pendientes, 'impacto pendiente', 'impactos pendientes')}
            </strong>
            : {impactos.data?.motivo_cierre ?? 'la versión no se puede cerrar hasta resolverlos.'}
          </span>
        </button>
      )}

      {vista === 'parrafos' && <BandejaPorBloque actividadId={actividadId} />}
      {vista === 'secciones' && <BandejaPorSeccion actividadId={actividadId} />}
      {vista === 'cambios' && (
        <VistaCambios
          actividadId={actividadId}
          onSintetizar={onSintetizar}
          corridaViva={corridaViva}
        />
      )}
      {vista === 'impactos' && <TableroImpactos actividadId={actividadId} />}
    </div>
  );
}

/**
 * LA BANDEJA POR SECCIÓN (V4).
 *
 * Por párrafo se lee el detalle; por sección se lee el mapa. Doce comentarios
 * repartidos en seis párrafos de un mismo capítulo son, leídos de a uno, seis
 * conversaciones sueltas; leídos juntos son «el capítulo de tomas de posesión
 * está en discusión». Esa lectura es la que decide qué se sintetiza primero.
 *
 * `GET /validaciones/{id}/secciones` es un GROUP BY sobre las anclas y la
 * jerarquía de títulos: determinista, sin IA, sin gastar cuota — agrupar es un
 * agregado mecánico y por regla dura del proyecto no entra a la cola de IA.
 *
 * El grupo `sin_ubicar` no se esconde ni se manda al final del scroll: son
 * voces que entraron y que nadie ubicó todavía. Callarlas es peor que mostrarlas.
 */
function BandejaPorSeccion({ actividadId }: { actividadId: string }) {
  const [abierta, setAbierta] = useState<string | null>(null);

  const q = useQuery({
    queryKey: cambiosKeys.secciones(actividadId),
    queryFn: () => cambiosApi.secciones(actividadId),
    refetchInterval: 30_000,
  });

  if (q.isLoading) return <EsqueletoLineas lineas={4} className="mt-2" />;
  if (q.isError) {
    return (
      <p className="mt-2 font-sans text-xs text-error">
        No pudimos agrupar por sección. Los comentarios están a salvo; reintentá en un
        momento.
      </p>
    );
  }
  // `null` (404) es «esta validación no tiene documento». No debería llegar acá
  // —esta vista solo se monta cuando lo tiene—, pero si el documento se
  // desvinculó mientras alguien miraba, lo honesto es decirlo y no fingir una
  // falla de red.
  if (!q.data) {
    return (
      <p className="mt-2 font-sans text-xs text-content-muted">
        Esta validación ya no tiene un documento cargado: los comentarios entran sobre
        el documento completo.
      </p>
    );
  }

  const d = q.data;
  if (d.secciones.length === 0) {
    return (
      <p className="mt-2 rounded-lg border border-dashed border-border px-3 py-2 font-sans text-sm text-content-muted">
        Todavía no entró ningún comentario en este documento.
      </p>
    );
  }

  return (
    <div className="despliega mt-2 rounded-lg border border-border bg-surface-raised p-2.5">
      <p className="font-sans text-[11px] uppercase tracking-wide text-content-muted">
        {plural(d.comentarios_total, 'comentario', 'comentarios')} en{' '}
        {plural(d.secciones_con_voces, 'sección', 'secciones')}
        {d.comentarios_sin_ubicar > 0 && ` · ${d.comentarios_sin_ubicar} sin ubicar`}
      </p>

      <ul className="mt-2 space-y-1.5">
        {d.secciones.map((s) => {
          const llave = `${s.clase}:${s.orden_inicio}:${s.titulo}`;
          const expandida = abierta === llave;
          const suelta = s.clase === 'sin_ubicar';
          return (
            <li
              key={llave}
              className={
                suelta
                  ? 'rounded-lg border border-warning bg-warning/5 px-2.5 py-2'
                  : 'rounded-lg bg-surface-sunken px-2.5 py-2'
              }
            >
              <button
                type="button"
                onClick={() => setAbierta(expandida ? null : llave)}
                aria-expanded={expandida}
                className="flex w-full items-start gap-2 text-left"
              >
                {expandida ? (
                  <ChevronDown className="mt-0.5 h-3.5 w-3.5 shrink-0 text-content-muted" aria-hidden="true" />
                ) : (
                  <ChevronRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-content-muted" aria-hidden="true" />
                )}
                <span className="min-w-0 flex-1">
                  <span className="block font-sans text-xs font-semibold leading-snug text-content">
                    {s.titulo}
                  </span>
                  {/* La ruta completa solo si aporta algo: repetir el título
                      como si fuera su propio capítulo es ruido. */}
                  {s.ruta.length > 1 && (
                    <span className="mt-0.5 block truncate font-sans text-[11px] text-content-muted">
                      {s.ruta.slice(0, -1).join(' › ')}
                    </span>
                  )}
                  <span className="mt-1 flex flex-wrap items-center gap-x-2.5 gap-y-0.5 font-sans text-[11px] text-content-muted">
                    <span className="inline-flex items-center gap-1 font-semibold text-content">
                      <FileCheck2 className="h-3 w-3" aria-hidden="true" />
                      {plural(s.comentarios, 'comentario', 'comentarios')}
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <FileText className="h-3 w-3" aria-hidden="true" />
                      en {plural(s.parrafos_tocados, 'párrafo', 'párrafos')}
                    </span>
                  </span>
                </span>
              </button>

              {suelta && (
                <p className="mt-1.5 pl-5 font-sans text-[11px] leading-snug text-content">
                  Estas voces hablan del documento, no de un párrafo. Alguien tiene que
                  decidir de dónde cuelgan; hasta entonces cuentan en el acta pero no en
                  ninguna sección.
                </p>
              )}

              {expandida && (
                <ul className="despliega mt-2 space-y-2 border-l-2 border-navy/20 pl-2.5">
                  {s.parrafos.map((p) => (
                    <li key={p.bloque_id_estable ?? `suelto-${p.orden}`}>
                      <p className="flex items-start gap-1.5">
                        <span className="shrink-0 rounded bg-navy/10 px-1.5 py-0.5 font-sans text-[11px] font-semibold text-navy">
                          {p.ref ?? (p.bloque_id_estable ? `¶ ${p.orden}` : 'Sin párrafo')}
                        </span>
                        <span className="min-w-0 flex-1 line-clamp-2 font-sans text-[11px] leading-relaxed text-content-muted">
                          {p.texto}
                        </span>
                      </p>
                      <ul className="mt-1 space-y-1.5 pl-1">
                        {p.comentarios.map((c) => (
                          <li key={c.aporte_id}>
                            <p className="font-sans text-[11px] font-semibold text-content">
                              {c.autor ?? 'Sin autor'}
                              {c.rol && (
                                <span className="ml-1.5 font-normal text-content-muted">
                                  · {c.rol}
                                </span>
                              )}
                              {c.tiene_audio && (
                                <span className="ml-1.5 font-normal text-content-muted">
                                  · de voz
                                </span>
                              )}
                              {c.origen_ancla === 'manual' && (
                                <span className="ml-1.5 font-normal text-content-muted">
                                  · ubicado por quien facilita
                                </span>
                              )}
                            </p>
                            <p className="font-sans text-xs leading-relaxed text-content-muted">
                              {c.texto}
                            </p>
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * LA ENTRADA AL DIFF DE CUATRO CAPAS (§5.8).
 *
 * El diff se pinta y se decide entero dentro de {@link DiffProcedencia}: pide
 * sus cambios, su documento y sus impactos con las llaves compartidas de
 * `lib/cambios`, y aprobar allá deja al día el tablero de impactos de acá sin
 * que estos dos componentes tengan que hablarse.
 *
 * Lo único que queda de este lado es el caso en que TODAVÍA NO HAY NADA: un
 * diff vacío no explica por qué está vacío ni qué hacer al respecto, y el botón
 * de sintetizar sí. Para saberlo se pide la misma consulta que el diff, con la
 * misma llave, así que no cuesta un viaje extra a la red.
 */
function VistaCambios({
  actividadId,
  onSintetizar,
  corridaViva,
}: {
  actividadId: string;
  onSintetizar: () => void;
  corridaViva: boolean;
}) {
  const diff = useQuery({
    queryKey: cambiosKeys.cambios(actividadId),
    queryFn: () => cambiosApi.diff(actividadId),
  });

  if (diff.isError) {
    return (
      <p className="mt-2 font-sans text-xs text-error">
        No pudimos traer los cambios propuestos. Reintentá en un momento.
      </p>
    );
  }

  if (!diff.isLoading && (diff.data?.total ?? 0) === 0) {
    return (
      <div className="despliega mt-2 rounded-lg border border-dashed border-border bg-surface-raised px-3 py-3">
        <p className="font-sans text-sm text-content-muted">
          Todavía no hay cambios propuestos. La síntesis lee de una sola vez todos los
          comentarios anclados y devuelve, por párrafo, qué se pide y quién lo pide. Las
          contradicciones no las resuelve: las deja escritas con quién sostiene cada
          postura, para que las dirima una persona.
        </p>
        <button
          type="button"
          onClick={onSintetizar}
          disabled={corridaViva}
          className="mt-2 inline-flex min-h-touch items-center gap-1.5 rounded-lg bg-violet px-4 py-2 font-sans text-sm font-semibold text-white transition hover:bg-violet-600 disabled:opacity-50"
        >
          {corridaViva ? (
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Sparkles className="h-4 w-4" aria-hidden="true" />
          )}
          {corridaViva ? 'Sintetizando…' : 'Sintetizar comentarios'}
        </button>
      </div>
    );
  }

  return (
    <div className="despliega mt-2">
      {/* `puedeDecidir` va prendido porque este panel es la Cabina y solo entra
          quien facilita. El servidor lo exige igual: esto evita ofrecer un
          botón que iba a terminar en 403, no reemplaza la autorización. */}
      <DiffProcedencia actividadId={actividadId} puedeDecidir />
    </div>
  );
}


/**
 * EL TABLERO DE IMPACTOS (V5 · SSOT §5.6).
 *
 * Aprobar un cambio no guarda un texto: contrae deudas. El grafo de contexto
 * —determinista, calculado sin IA— sabe que corregir RF-064 deja mintiendo a
 * RN-011 aunque RN-011 no lo nombre por su código, y abre un impacto ahí.
 *
 * Dos reglas gobiernan esta pantalla y ninguna es cosmética:
 *
 *   D07 · Cerrar un impacto SIN cambiar nada obliga a escribir la razón. Por eso
 *   el botón nace deshabilitado y se enciende con lo que se escriba: la
 *   restricción tiene que verse antes de apretar, no como un 422 después.
 *   Dentro de un año el expediente tiene que poder decir que alguien miró ese
 *   párrafo y decidió dejarlo como estaba, y por qué.
 *
 *   D08 · Con impactos pendientes la versión no se cierra. Eso va arriba de
 *   todo, no al final.
 *
 * Y el riesgo R04 —fatiga de alertas— es la razón de que cada impacto muestre
 * POR QUÉ se abrió, de qué vía y con cuánta confianza. «Descartar» es un botón
 * de primera clase, no un castigo: un falso positivo que no se puede descartar
 * enseña a cerrar todo sin leer, que es exactamente como muere esta función.
 */
function TableroImpactos({ actividadId }: { actividadId: string }) {
  const qc = useQueryClient();
  const [razones, setRazones] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const q = useQuery({
    queryKey: cambiosKeys.impactos(actividadId),
    queryFn: () => cambiosApi.impactos(actividadId),
    refetchInterval: 60_000,
  });

  const resolver = useMutation({
    mutationFn: (v: { id: string; cuerpo: ResolucionImpacto }) =>
      cambiosApi.resolverImpacto(v.id, v.cuerpo),
    onMutate: () => setError(null),
    onSuccess: (_r, v) => {
      setRazones((r) => {
        const resto = { ...r };
        delete resto[v.id];
        return resto;
      });
      void qc.invalidateQueries({ queryKey: cambiosKeys.impactos(actividadId) });
      void qc.invalidateQueries({ queryKey: ['validacion-cambios', actividadId] });
    },
    onError: (e) =>
      setError(mensajeDeError(e, 'No pudimos cerrar ese impacto. Reintentá en un momento.')),
  });

  if (q.isLoading) return <EsqueletoLineas lineas={4} className="mt-2" />;
  if (q.isError) {
    return (
      <p className="mt-2 font-sans text-xs text-error">
        No pudimos traer los impactos. Reintentá en un momento.
      </p>
    );
  }
  // `null` (404) es «esta validación no tiene documento». No debería llegar acá
  // —esta vista solo se monta cuando lo tiene—, pero si el documento se
  // desvinculó mientras alguien miraba, lo honesto es decirlo y no fingir una
  // falla de red.
  if (!q.data) {
    return (
      <p className="mt-2 font-sans text-xs text-content-muted">
        Esta validación ya no tiene un documento cargado, así que no hay impactos que
        seguir.
      </p>
    );
  }

  const d = q.data;

  return (
    <div className="despliega mt-2 rounded-lg border border-border bg-surface-raised p-2.5">
      {/* D08 · lo primero que se lee. */}
      <p
        className={
          d.puede_cerrarse
            ? 'flex items-start gap-1.5 rounded-lg bg-success/10 px-2.5 py-1.5 font-sans text-xs text-success'
            : 'flex items-start gap-1.5 rounded-lg border border-warning bg-warning/5 px-2.5 py-1.5 font-sans text-xs text-content'
        }
      >
        {d.puede_cerrarse ? (
          <Check className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        ) : (
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" aria-hidden="true" />
        )}
        <span>{d.motivo_cierre}</span>
      </p>

      {error && (
        <p role="alert" className="mt-1.5 rounded-lg border border-error px-2.5 py-1.5 font-sans text-xs text-error">
          {error}
        </p>
      )}

      {d.total === 0 ? (
        <p className="mt-2 rounded-lg border border-dashed border-border px-3 py-2 font-sans text-sm text-content-muted">
          Ningún cambio aprobado todavía, así que no hay nada abierto. Los impactos
          aparecen al aprobar un cambio: el sistema recorre el documento y avisa qué
          otros párrafos quedan tocados.
        </p>
      ) : (
        <ul className="mt-2 space-y-2">
          {d.grupos.map((g) => (
            <li key={g.cambio_id} className="rounded-lg bg-surface-sunken px-2.5 py-2">
              <p className="flex flex-wrap items-center gap-1.5">
                <span className="shrink-0 rounded bg-navy/10 px-1.5 py-0.5 font-sans text-[11px] font-semibold text-navy">
                  {g.ref ?? 'Párrafo'}
                </span>
                <span className="font-sans text-[11px] text-content-muted">
                  abrió {plural(g.impactos.length, 'impacto', 'impactos')}
                  {g.abiertos > 0 && ` · ${g.abiertos} sin resolver`}
                </span>
              </p>
              <p className="mt-1 line-clamp-2 font-sans text-xs leading-relaxed text-content-muted">
                {g.texto}
              </p>

              <ul className="mt-2 space-y-1.5 border-l-2 border-warning/40 pl-2.5">
                {g.impactos.map((i) => {
                  const razon = razones[i.id] ?? '';
                  const trabajando = resolver.isPending && resolver.variables?.id === i.id;
                  return (
                    <li
                      key={i.id}
                      className="rounded-lg border border-border bg-surface-raised px-2.5 py-2"
                    >
                      <p className="flex flex-wrap items-center gap-1.5">
                        <span className="shrink-0 rounded bg-warning/15 px-1.5 py-0.5 font-sans text-[11px] font-semibold text-warning">
                          {i.etiqueta}
                        </span>
                        {/* La profundidad no es un detalle técnico: «a dos
                            saltos» es el cruce que nadie iba a encontrar a mano
                            y también el que más falsos positivos deja. */}
                        <span className="font-sans text-[10px] uppercase tracking-wide text-content-muted">
                          a {plural(i.profundidad, 'salto', 'saltos')}
                        </span>
                        {i.estado !== 'pendiente' && (
                          <span className="rounded-full bg-border px-2 py-0.5 font-sans text-[10px] font-semibold text-content-muted">
                            {ETIQUETA_ESTADO_IMPACTO[i.estado] ?? i.estado}
                          </span>
                        )}
                      </p>

                      {i.arista && (
                        <p className="mt-1 font-sans text-[11px] leading-snug text-content-muted">
                          {NOMBRE_ARISTA[i.arista.tipo] ?? i.arista.tipo}
                          {i.arista.confianza !== null && (
                            <> · confianza {Math.round(i.arista.confianza * 100)}%</>
                          )}
                          {i.porque && <>: «{i.porque}»</>}
                        </p>
                      )}

                      {i.estado === 'pendiente' ? (
                        <div className="mt-1.5">
                          <label className="flex flex-col gap-1 font-sans text-[11px] text-content-muted">
                            Por qué se deja como está (obligatorio para cerrar sin cambiar
                            nada)
                            <textarea
                              value={razon}
                              onChange={(e) =>
                                setRazones((r) => ({ ...r, [i.id]: e.target.value }))
                              }
                              rows={2}
                              placeholder="El plazo de RN-011 ya contempla la suspensión sin goce: no hay que tocarlo."
                              className="rounded-lg border border-border bg-surface-base px-2.5 py-1.5 font-sans text-xs text-content placeholder:text-content-muted focus:border-navy focus:outline-none"
                            />
                          </label>
                          <div className="mt-1.5 flex flex-wrap gap-1.5">
                            <button
                              type="button"
                              disabled={razon.trim().length === 0 || trabajando}
                              onClick={() =>
                                resolver.mutate({
                                  id: i.id,
                                  cuerpo: {
                                    estado: 'revisado_sin_cambio',
                                    razon: razon.trim(),
                                  },
                                })
                              }
                              className="inline-flex min-h-touch items-center gap-1.5 rounded-lg border border-navy px-3 py-2 font-sans text-xs font-semibold text-navy transition hover:bg-navy/5 disabled:opacity-40"
                            >
                              {trabajando ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                              ) : (
                                <Check className="h-3.5 w-3.5" aria-hidden="true" />
                              )}
                              Revisado, sin cambio
                            </button>
                            <button
                              type="button"
                              disabled={trabajando}
                              onClick={() =>
                                resolver.mutate({
                                  id: i.id,
                                  cuerpo: {
                                    estado: 'descartado',
                                    ...(razon.trim() ? { razon: razon.trim() } : {}),
                                  },
                                })
                              }
                              className="inline-flex min-h-touch items-center gap-1.5 rounded-lg border border-border-strong px-3 py-2 font-sans text-xs font-semibold text-content-muted transition hover:border-error hover:text-error disabled:opacity-40"
                            >
                              <X className="h-3.5 w-3.5" aria-hidden="true" />
                              No aplica: falso aviso
                            </button>
                          </div>
                          <p className="mt-1 font-sans text-[10px] leading-snug text-content-muted">
                            Descartar no pide razón, pero escribirla ayuda a calibrar de qué
                            vía llegan los avisos que sobran.
                          </p>
                        </div>
                      ) : (
                        <p className="mt-1 font-sans text-[11px] leading-snug text-content-muted">
                          {i.razon && <span className="text-content">«{i.razon}» </span>}
                          {i.resuelto_por_nombre && <>— {i.resuelto_por_nombre}</>}
                          {i.resuelto_at && <> · {i.resuelto_at.slice(0, 10)}</>}
                        </p>
                      )}
                    </li>
                  );
                })}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
